import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import Input from '../../components/ui/Input';
import Select from '../../components/ui/Select';
import Textarea from '../../components/ui/Textarea';
import {
  createInboundRouter,
  getInboundRouter,
  improveInboundRouteDescription,
  improveInboundRouterInstructions,
  updateInboundRouter,
} from '../../api/inboundRouters';
import { getAgent } from '../../api/agents';
import { listMcpServers } from '../../api/mcpServers';
import { getApiErrorMessage } from '../../utils/errors';
import ImprovePreviewModal from '../Onboarding/components/ImprovePreviewModal';
import ImproveTextModal from '../Onboarding/components/ImproveTextModal';
import McpServerBindingsEditor from '../Onboarding/components/McpServerBindingsEditor';
import AgentRoutesEditor from './components/AgentRoutesEditor';
import ChannelAnchorSelect from './components/ChannelAnchorSelect';
import {
  buildInboundRouterPayload,
  EMPTY_INBOUND_ROUTER_FORM,
  mapInboundRouterFromApi,
  MCP_EXECUTION_MODE_OPTIONS,
  parseKeywordsInput,
  ROUTER_STATUS_OPTIONS,
} from './constants';
import { findChannelGroupByAnchorId } from './utils/channelOptions';
import { resolveMcpAvailability } from './utils/mcpAvailability';
import { fetchChannelOptionGroups } from './utils/fetchChannelOptionGroups';

function InboundRouterForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = Boolean(id);

  const [form, setForm] = useState(EMPTY_INBOUND_ROUTER_FORM);
  const [channelGroups, setChannelGroups] = useState([]);
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [routesError, setRoutesError] = useState('');
  const [mcpBindingsError, setMcpBindingsError] = useState('');
  const [mcpServers, setMcpServers] = useState([]);
  const [mcpServersNotice, setMcpServersNotice] = useState('');
  const [mcpServersLoadError, setMcpServersLoadError] = useState('');
  const [instructionsImproveError, setInstructionsImproveError] = useState('');
  const [routeDescriptionImproveErrors, setRouteDescriptionImproveErrors] = useState({});
  const [improveModalKind, setImproveModalKind] = useState('inbound_router_instructions');
  const [pendingImproveRoute, setPendingImproveRoute] = useState(null);
  const [isImproveModalOpen, setIsImproveModalOpen] = useState(false);
  const [isImproveModalSubmitting, setIsImproveModalSubmitting] = useState(false);
  const [improveModalError, setImproveModalError] = useState('');
  const [isImprovingInstructions, setIsImprovingInstructions] = useState(false);
  const [improvingRouteId, setImprovingRouteId] = useState(null);
  const [improvePreview, setImprovePreview] = useState(null);
  const [isApplyingImprovePreview, setIsApplyingImprovePreview] = useState(false);
  const [improvePreviewError, setImprovePreviewError] = useState('');

  const agentRoutesForImprovement = useMemo(
    () =>
      (form.agentRoutes || [])
        .filter((route) => route.agentId && route.label?.trim())
        .map((route) => ({
          agent_id: route.agentId,
          label: route.label.trim(),
          description: (route.description || '').trim(),
          keywords: parseKeywordsInput(route.keywordsInput),
        })),
    [form.agentRoutes]
  );

  const isImproveBusy =
    isImproveModalSubmitting ||
    isImprovingInstructions ||
    Boolean(improvingRouteId) ||
    isApplyingImprovePreview;

  const defaultAgentOptions = useMemo(() => {
    const options = [{ value: '', label: 'Nenhum (perguntar ao usuário)' }];
    for (const route of form.agentRoutes || []) {
      if (route.agentId && route.label?.trim()) {
        options.push({
          value: route.agentId,
          label: route.label.trim(),
        });
      }
    }
    return options;
  }, [form.agentRoutes]);

  const buildOtherRoutesForImprovement = useCallback(
    (targetRouteId) =>
      (form.agentRoutes || [])
        .filter(
          (route) =>
            route.id !== targetRouteId && route.label?.trim()
        )
        .map((route) => ({
          agent_id: route.agentId || undefined,
          label: route.label.trim(),
          description: (route.description || '').trim(),
          keywords: parseKeywordsInput(route.keywordsInput),
        })),
    [form.agentRoutes]
  );

  const selectedGroup = useMemo(
    () => findChannelGroupByAnchorId(channelGroups, form.channelId),
    [channelGroups, form.channelId]
  );

  const allowedAgentIds = selectedGroup?.agentIds || null;

  const loadEntry = useCallback(async () => {
    if (!id) {
      return;
    }

    setLoading(true);
    setError('');
    try {
      const entry = await getInboundRouter(id);
      setForm(mapInboundRouterFromApi(entry));
    } catch (err) {
      setError(getApiErrorMessage(err, 'Não foi possível carregar o roteamento.'));
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    loadEntry();
  }, [loadEntry]);

  useEffect(() => {
    let cancelled = false;
    listMcpServers({ page: 1, perPage: 100 })
      .then((response) => {
        if (!cancelled) {
          const { selectable, notice } = resolveMcpAvailability(response.data);
          setMcpServers(selectable);
          setMcpServersNotice(notice);
          setMcpServersLoadError('');
        }
      })
      .catch((err) => {
        if (!cancelled) {
          setMcpServers([]);
          setMcpServersNotice('');
          setMcpServersLoadError(
            getApiErrorMessage(
              err,
              'Não foi possível carregar os servidores MCP desta conta. Recarregue a página para tentar de novo.'
            )
          );
        }
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!isEdit) {
      return;
    }

    let cancelled = false;
    fetchChannelOptionGroups()
      .then(({ groups }) => {
        if (!cancelled) {
          setChannelGroups(groups);
        }
      })
      .catch(() => {});

    return () => {
      cancelled = true;
    };
  }, [isEdit]);

  const handleChange = (field) => (event) => {
    setForm((current) => ({ ...current, [field]: event.target.value }));
    setSuccess('');
    setRoutesError('');
    if (field === 'instructions' && instructionsImproveError) {
      setInstructionsImproveError('');
    }
  };

  const handleCloseImproveModal = () => {
    if (isImproveModalSubmitting) {
      return;
    }
    setIsImproveModalOpen(false);
    setImproveModalError('');
    setPendingImproveRoute(null);
  };

  const handleOpenImproveModal = () => {
    const draft = form.instructions.trim();
    if (!draft) {
      setInstructionsImproveError('Escreva um rascunho antes de melhorar o texto.');
      return;
    }

    setInstructionsImproveError('');
    setImproveModalError('');
    setImproveModalKind('inbound_router_instructions');
    setPendingImproveRoute(null);
    setIsImproveModalOpen(true);
  };

  const handleRequestImproveDescription = (route) => {
    const label = route.label?.trim();
    if (!label) {
      setRouteDescriptionImproveErrors((current) => ({
        ...current,
        [route.id]: 'Preencha o rótulo antes de melhorar a descrição.',
      }));
      return;
    }

    setRouteDescriptionImproveErrors((current) => {
      if (!current[route.id]) {
        return current;
      }
      const next = { ...current };
      delete next[route.id];
      return next;
    });
    setImproveModalError('');
    setImproveModalKind('inbound_route_description');
    setPendingImproveRoute(route);
    setIsImproveModalOpen(true);
  };

  const getRouteDescriptionImproveError = useCallback(
    (routeId) => routeDescriptionImproveErrors[routeId] || '',
    [routeDescriptionImproveErrors]
  );

  const handleConfirmImproveModal = async (userConsiderations) => {
    setImproveModalError('');
    setIsImproveModalSubmitting(true);

    try {
      if (improveModalKind === 'inbound_route_description') {
        if (!pendingImproveRoute) {
          throw new Error('Destino não selecionado para melhoria.');
        }

        const route = pendingImproveRoute;
        const draft = (route.description || '').trim();
        const keywords = parseKeywordsInput(route.keywordsInput);
        setImprovingRouteId(route.id);

        let agentName;
        if (route.agentId) {
          try {
            const agent = await getAgent(route.agentId);
            agentName = agent?.name || undefined;
          } catch {
            agentName = undefined;
          }
        }

        const { text } = await improveInboundRouteDescription({
          text: draft,
          routeLabel: route.label.trim(),
          agentId: route.agentId || undefined,
          agentName,
          keywords,
          routerName: form.name.trim() || undefined,
          routerInstructions: form.instructions.trim() || undefined,
          otherRoutes: buildOtherRoutesForImprovement(route.id),
          userConsiderations: userConsiderations || undefined,
        });

        setImprovePreview({
          kind: 'inbound_route_description',
          routeId: route.id,
          previousText: draft,
          improvedText: text,
          userConsiderations: userConsiderations || '',
        });
      } else {
        setIsImprovingInstructions(true);
        const draft = form.instructions.trim();
        const { text } = await improveInboundRouterInstructions({
          text: draft,
          routerName: form.name.trim() || undefined,
          agentRoutes: agentRoutesForImprovement,
          userConsiderations: userConsiderations || undefined,
        });
        setImprovePreview({
          kind: 'inbound_router_instructions',
          previousText: draft,
          improvedText: text,
          userConsiderations: userConsiderations || '',
        });
      }

      setIsImproveModalOpen(false);
      setPendingImproveRoute(null);
    } catch (err) {
      setImproveModalError(
        getApiErrorMessage(err, 'Não foi possível melhorar o texto agora. Tente novamente.')
      );
    } finally {
      setIsImproveModalSubmitting(false);
      setIsImprovingInstructions(false);
      setImprovingRouteId(null);
    }
  };

  const handleDiscardImprovePreview = () => {
    if (isApplyingImprovePreview) {
      return;
    }
    setImprovePreview(null);
    setImprovePreviewError('');
  };

  const handleBackToImproveConsiderations = () => {
    if (isApplyingImprovePreview || !improvePreview) {
      return;
    }

    const preview = improvePreview;
    setImprovePreview(null);
    setImprovePreviewError('');
    setImproveModalKind(preview.kind || 'inbound_router_instructions');
    if (preview.routeId) {
      const route = form.agentRoutes.find((item) => item.id === preview.routeId);
      setPendingImproveRoute(route || null);
    } else {
      setPendingImproveRoute(null);
    }
    setIsImproveModalOpen(true);
  };

  const handleApplyImprovePreview = () => {
    if (!improvePreview) {
      return;
    }

    if (improvePreview.routeId) {
      setForm((current) => ({
        ...current,
        agentRoutes: current.agentRoutes.map((route) =>
          route.id === improvePreview.routeId
            ? { ...route, description: improvePreview.improvedText }
            : route
        ),
      }));
      setRouteDescriptionImproveErrors((current) => {
        if (!current[improvePreview.routeId]) {
          return current;
        }
        const next = { ...current };
        delete next[improvePreview.routeId];
        return next;
      });
    } else {
      setForm((current) => ({
        ...current,
        instructions: improvePreview.improvedText,
      }));
      setInstructionsImproveError('');
    }

    setImprovePreview(null);
    setImprovePreviewError('');
    setSuccess('');
  };

  const handleChannelGroupsLoaded = useCallback((groups) => {
    setChannelGroups(groups);
  }, []);

  const validateRoutes = () => {
    const routes = form.agentRoutes || [];
    if (routes.length === 0) {
      setRoutesError('Adicione pelo menos um destino.');
      return false;
    }

    const agentIds = routes.map((route) => route.agentId).filter(Boolean);
    if (agentIds.length !== routes.length) {
      setRoutesError('Selecione um agente em cada destino.');
      return false;
    }

    if (new Set(agentIds).size !== agentIds.length) {
      setRoutesError('Cada agente só pode aparecer uma vez.');
      return false;
    }

    const missingLabel = routes.some((route) => !route.label?.trim());
    if (missingLabel) {
      setRoutesError('Preencha o rótulo de cada destino.');
      return false;
    }

    if (allowedAgentIds?.length) {
      const invalid = agentIds.some((agentId) => !allowedAgentIds.includes(agentId));
      if (invalid) {
        setRoutesError('Todos os agentes devem pertencer ao WhatsApp selecionado.');
        return false;
      }
    }

    setRoutesError('');
    return true;
  };

  const validateMcpBindings = () => {
    const bindings = form.mcpBindings || [];
    if (form.mcpExecutionMode === 'required' && bindings.length === 0) {
      setMcpBindingsError(
        'Vincule ao menos um servidor MCP para tornar a consulta obrigatória.'
      );
      return false;
    }
    if (bindings.some((binding) => !binding.mcpServerId)) {
      setMcpBindingsError('Selecione um servidor em cada vínculo MCP.');
      return false;
    }
    if (bindings.some((binding) => !binding.instructions?.trim())) {
      setMcpBindingsError('Preencha as instruções de uso de cada MCP.');
      return false;
    }
    const serverIds = bindings.map((binding) => binding.mcpServerId);
    if (new Set(serverIds).size !== serverIds.length) {
      setMcpBindingsError('Cada servidor MCP só pode aparecer uma vez.');
      return false;
    }
    setMcpBindingsError('');
    return true;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!validateRoutes() || !validateMcpBindings()) {
      return;
    }

    setSaving(true);
    setError('');
    setSuccess('');

    try {
      const payload = buildInboundRouterPayload(form, { isEdit });
      if (isEdit) {
        await updateInboundRouter(id, payload);
        setSuccess('Roteamento atualizado.');
        await loadEntry();
      } else {
        if (!form.channelId) {
          setError('Selecione o canal âncora WhatsApp.');
          setSaving(false);
          return;
        }
        const created = await createInboundRouter(payload);
        navigate(`/inbound-routers/${created.id}/edit`, {
          replace: true,
          state: { created: true },
        });
      }
    } catch (err) {
      setError(
        getApiErrorMessage(
          err,
          'Não foi possível salvar o roteamento. Verifique se já existe um roteamento para este canal WhatsApp.'
        )
      );
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="surface-card px-4 py-12 text-center max-w-xl mx-auto">
        <p className="text-content-muted text-sm">Carregando roteamento...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-2xl mx-auto">
      <div>
        <Link to="/inbound-routers" className="text-sm text-content-muted hover:text-forge-600">
          ← Voltar para roteamentos
        </Link>
        <h1 className="text-2xl font-bold text-content-primary mt-3">
          {isEdit ? 'Editar roteamento' : 'Novo roteamento'}
        </h1>
        <p className="text-content-muted text-sm mt-1">
          Defina regras para direcionar mensagens ao agente correto no mesmo WhatsApp.
        </p>
      </div>

      <div className="surface-card p-8 shadow-card-md">
        {error && (
          <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 mb-4">
            <p className="text-sm text-red-700">{error}</p>
          </div>
        )}
        {success && (
          <div className="rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 mb-4">
            <p className="text-sm text-emerald-700">{success}</p>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-6">
          <Input
            id="name"
            label="Nome"
            name="name"
            value={form.name}
            onChange={handleChange('name')}
            placeholder="Ex.: Roteamento principal"
            required
          />

          {isEdit ? (
            <Input
              id="channel-readonly"
              label="Canal âncora (WhatsApp)"
              value={form.externalChannelId || form.channelId}
              disabled
              hint="O canal âncora não pode ser alterado após a criação."
            />
          ) : (
            <ChannelAnchorSelect
              value={form.channelId}
              onChange={handleChange('channelId')}
              onGroupsLoaded={handleChannelGroupsLoaded}
            />
          )}

          <Textarea
            id="instructions"
            label="Instruções de roteamento"
            value={form.instructions}
            onChange={handleChange('instructions')}
            placeholder="Descreva quando escolher um agente, usar um MCP e encerrar, ou usar um MCP antes de encaminhar..."
            rows={4}
            hint="Usadas pela IA quando palavras-chave e regras determinísticas não forem suficientes. Com MCP vinculado, explique quando chamar uma ferramenta e encerrar ou quando encaminhar o resultado a um agente."
            action={{
              iconOnly: true,
              ariaLabel: 'Melhorar instruções de roteamento com IA',
              loading: isImprovingInstructions || (isImproveModalSubmitting && improveModalKind === 'inbound_router_instructions'),
              disabled: saving || isImproveBusy,
              onClick: handleOpenImproveModal,
              error: instructionsImproveError,
            }}
          />

          <AgentRoutesEditor
            routes={form.agentRoutes}
            onChange={(agentRoutes) => {
              setForm((current) => {
                const next = { ...current, agentRoutes };
                const ids = agentRoutes
                  .map((route) => route.agentId)
                  .filter(Boolean);
                if (
                  current.defaultAgentId &&
                  !ids.includes(current.defaultAgentId)
                ) {
                  next.defaultAgentId = '';
                }
                if (
                  current.mcpFailureAgentId &&
                  !ids.includes(current.mcpFailureAgentId)
                ) {
                  next.mcpFailureAgentId = '';
                }
                return next;
              });
              setRoutesError('');
              setSuccess('');
            }}
            allowedAgentIds={allowedAgentIds}
            error={routesError}
            onRequestImproveDescription={handleRequestImproveDescription}
            improvingRouteId={improvingRouteId}
            isImproveBusy={isImproveBusy}
            getRouteDescriptionImproveError={getRouteDescriptionImproveError}
          />

          <McpServerBindingsEditor
            id="router_mcp_bindings"
            label="Servidores MCP disponíveis neste roteador"
            hint="Selecione os MCPs que a IA pode usar e explique como usar suas ferramentas. As instruções gerais acima decidem entre somente MCP, somente agente ou MCP seguido de agente."
            mcpServers={mcpServers}
            bindings={form.mcpBindings || []}
            onChange={(mcpBindings) => {
              setForm((current) => ({ ...current, mcpBindings }));
              setMcpBindingsError('');
              setSuccess('');
            }}
            error={mcpBindingsError}
            allowContactScoped={false}
            loadError={mcpServersLoadError}
            notice={mcpServersNotice}
          />

          <Select
            id="mcpExecutionMode"
            label="Consulta MCP antes do roteamento"
            value={form.mcpExecutionMode || 'optional'}
            onChange={handleChange('mcpExecutionMode')}
            options={MCP_EXECUTION_MODE_OPTIONS}
            hint="No modo obrigatório, o roteador chama ao menos uma ferramenta antes de escolher o destino, inclusive em mensagens genéricas como “oi”."
          />

          {form.mcpExecutionMode === 'required' && (
            <Select
              id="mcpFailureAgentId"
              label="Destino se o MCP falhar"
              value={form.mcpFailureAgentId || ''}
              onChange={handleChange('mcpFailureAgentId')}
              options={defaultAgentOptions}
              hint="Recomendado: escolha o agente de identificação. Se ficar vazio, o roteador pergunta ao usuário como pode ajudar."
            />
          )}

          <Select
            id="defaultAgentId"
            label="Agente padrão"
            value={form.defaultAgentId || ''}
            onChange={handleChange('defaultAgentId')}
            options={defaultAgentOptions}
            hint="Usado quando as instruções e a mensagem não deixam o destino claro. Se vazio, o sistema pergunta ao usuário."
          />

          <Input
            id="clarificationMessage"
            label="Mensagem de clarificação"
            name="clarificationMessage"
            value={form.clarificationMessage}
            onChange={handleChange('clarificationMessage')}
            placeholder="Ex.: Com qual área você gostaria de falar?"
            hint={
              form.defaultAgentId
                ? 'Só é enviada se não houver agente padrão e o destino continuar ambíguo.'
                : 'Enviada quando o roteador não conseguir decidir o destino automaticamente.'
            }
          />

          <Textarea
            id="toneOfVoice"
            label="Tom de voz da clarificação"
            value={form.toneOfVoice}
            onChange={handleChange('toneOfVoice')}
            placeholder="Ex.: acolhedor e informal, tratando a pessoa por você e com frases curtas..."
            rows={3}
            hint="Aplicado apenas à mensagem de clarificação, para manter o mesmo clima da marca durante o roteamento. Não influencia a escolha do agente. Se vazio, a mensagem é enviada sem ajuste de tom."
          />

          <Select
            id="status"
            label="Status"
            value={form.status}
            onChange={handleChange('status')}
            options={ROUTER_STATUS_OPTIONS}
          />

          <div className="flex flex-col sm:flex-row gap-3 pt-2">
            <button
              type="submit"
              disabled={saving || isImproveBusy}
              className="btn-primary text-sm px-4 py-2.5"
            >
              {saving ? 'Salvando...' : isEdit ? 'Salvar alterações' : 'Criar roteamento'}
            </button>
          </div>
        </form>
      </div>

      <ImproveTextModal
        open={isImproveModalOpen}
        kind={improveModalKind}
        isSubmitting={isImproveModalSubmitting}
        error={improveModalError}
        onClose={handleCloseImproveModal}
        onSubmit={handleConfirmImproveModal}
      />

      <ImprovePreviewModal
        open={Boolean(improvePreview)}
        kind={improvePreview?.kind || 'inbound_router_instructions'}
        previousText={improvePreview?.previousText || ''}
        improvedText={improvePreview?.improvedText || ''}
        isApplying={isApplyingImprovePreview}
        error={improvePreviewError}
        onDiscard={handleDiscardImprovePreview}
        onBackToConsiderations={handleBackToImproveConsiderations}
        onApply={handleApplyImprovePreview}
      />
    </div>
  );
}

export default InboundRouterForm;
